<template>
  <div>
    <section class="">
      <div class="bg-pattern overlay-fppc logReg-container">
        <div class="col-sm-12 col-md-12 col-lg-4 col-xl-4 col-xxl-4 mx-auto">
          <!--forgot password block start-->
          <div class="card card-form sign-upIn">
            <div class="card-header">
              <div class="card-title">Forgot password</div>
            </div>
            <div class="card-body">
              <!-- success state -->
              <div v-if="sent" class="base-form">
                <h6 class="form-title">Check your inbox for a reset link</h6>
                <div class="block-ntfmsg">
                  <p>
                    If <strong>{{ sentTo }}</strong> is registered, we've sent
                    instructions to reset your password. It can take a few
                    minutes to arrive; check your spam folder too.
                  </p>
                </div>

                <div v-if="error" class="text-danger small mb-2" role="alert">{{ error }}</div>
                <div v-if="rateLimited" class="text-danger small mb-2" role="alert">{{ rateLimitText }}</div>

                <div class="form-footer align-items-end gap-2">
                  <button type="button" class="btn btn-outline-secondary" @click="useDifferentEmail" :disabled="loading">
                    Use a different email
                  </button>
                  <button type="button" class="btn btn-success" @click="resend" :disabled="resendDisabled">
                    <template v-if="loading">Sending</template>
                    <template v-else-if="resendSeconds > 0">Resend in {{ resendSeconds }}s</template>
                    <template v-else>Resend</template>
                    <span v-show="loading" class="spinner-border spinner-border-sm text-light ms-2" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </span>
                  </button>
                </div>
              </div>

              <form v-else @submit.prevent="handleSubmit" class="base-form" novalidate>
                <h6 class="form-title">Enter registered email address</h6>

                <div class="input-group _ig-transparent mb-1">
                  <span class="input-group-text"
                    ><i class="fa fa-user"></i>
                  </span>
                  <input
                    type="email"
                    v-model="form.email"
                    id="reg_email"
                    name="reg_email"
                    class="form-control"
                    :class="{ 'is-invalid': error }"
                    placeholder="Enter your email"
                    autocomplete="email"
                    :disabled="loading"
                    @input="error = ''"
                    required
                  />
                </div>
                <div v-if="error" class="text-danger small mb-2" role="alert">{{ error }}</div>
                <div v-if="rateLimited" class="text-danger small mb-2" role="alert">{{ rateLimitText }}</div>

                <span class="form-text d-block mt-2"
                  >If the provided information is correct, we will send you an
                  instruction to reset your password</span
                >

                <div class="form-footer align-items-end">
                  <button type="submit" name="signIn" class="btn btn-success" :disabled="loading || rateLimited">
                    {{ loading ? "Sending" : "Next" }}
                    <span v-show="loading" class="spinner-border spinner-border-sm text-light ms-2" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
          <!--ended forgot password-->
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import { firstMessage, rateLimitMixin } from "@/utils/rateLimit";

const RESEND_COOLDOWN_SECONDS = 60;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "ForgotPassword",
  mixins: [rateLimitMixin],

  data() {
    return {
      loading: false,
      sent: false,
      sentTo: "",
      error: "",
      resendSeconds: 0,
      resendTimer: null,
      form: {
        email: "",
      },
    };
  },
  computed: {
    resendDisabled() {
      return this.loading || this.rateLimited || this.resendSeconds > 0;
    },
  },
  beforeUnmount() {
    this.stopResendCooldown();
  },
  methods: {
    handleSubmit() {
      const email = this.form.email.trim().toLowerCase();
      this.form.email = email;
      if (!EMAIL_PATTERN.test(email)) {
        this.error = "Please enter a valid email address.";
        return;
      }
      this.send(email);
    },
    resend() {
      if (this.resendDisabled) return;
      this.send(this.sentTo);
    },
    useDifferentEmail() {
      this.sent = false;
      this.error = "";
      this.stopResendCooldown();
    },
    send(email) {
      // One request at a time; also blocked while a 429 countdown runs.
      if (this.loading || this.rateLimited) return;
      this.loading = true;
      this.error = "";

      this.$store
        .dispatch("auth/forgotPassword", { email })
        .then((res) => {
          if (res && res.status == 1) {
            this.sent = true;
            this.sentTo = email;
            this.startResendCooldown();
          } else {
            this.error = firstMessage(res) || "We couldn't send a reset link. Please try again.";
          }
        })
        .catch((err) => {
          if (this.handleRateLimit(err)) return;
          if (err && err.code === "ECONNABORTED") {
            this.error = "This is taking longer than expected. Please check your inbox before trying again.";
          } else if (err && err.response) {
            this.error = firstMessage(err.response.data) || "Something went wrong. Please try again.";
          } else {
            this.error = "Couldn't reach the server. Check your connection and try again.";
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startResendCooldown() {
      this.stopResendCooldown();
      const endsAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      const tick = () => {
        this.resendSeconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
        if (this.resendSeconds === 0) this.stopResendCooldown();
      };
      tick();
      this.resendTimer = setInterval(tick, 250);
    },
    stopResendCooldown() {
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendTimer = null;
      this.resendSeconds = 0;
    },
  },
};
</script>
